// src/features/secureDocuments/components/LohnlaufPruefungPanel.tsx
//
// Anzeige des Prüfergebnisses im Upload-Modal. Reine Darstellung, keine Logik.
// Erscheint nur, wenn in der Dateiauswahl DATEV-Lohnlaufunterlagen erkannt
// wurden; bei allen anderen Uploads bleibt das Modal unverändert.

import React from 'react';
import { AlertCircle, CheckCircle2, HelpCircle, Info, Loader2, ShieldCheck, XCircle } from 'lucide-react';
import { Befund, PruefErgebnis, Pruefstufe, euro, ibanLesbar, isoZuDeutsch } from '../validation/lohnlaufPruefung';

interface LohnlaufPruefungPanelProps {
  ergebnis: PruefErgebnis | null;
  laeuft: boolean;
  /**
   * Wurde die Upload-Sperre im Modal für diesen einen Upload übergangen,
   * ändert sich ausschließlich der Hinweistext in der Kopfzeile. Die Befunde
   * bleiben unverändert stehen.
   */
  sperreAufgehoben?: boolean;
}

const STUFEN_STIL: Record<Pruefstufe, { rahmen: string; flaeche: string; schrift: string }> = {
  sperre: { rahmen: '#FECACA', flaeche: '#FEF2F2', schrift: '#991B1B' },
  warnung: { rahmen: '#FDE68A', flaeche: '#FFFBEB', schrift: '#92400E' },
  ungeprueft: { rahmen: '#DDD6FE', flaeche: '#F5F3FF', schrift: '#5B21B6' },
  hinweis: { rahmen: '#BAE6FD', flaeche: '#F0F9FF', schrift: '#075985' },
  ok: { rahmen: '#BBF7D0', flaeche: '#F0FDF4', schrift: '#166534' }
};

const stufenSymbol = (stufe: Pruefstufe, farbe: string) => {
  switch (stufe) {
    case 'sperre': return <XCircle size={16} color={farbe} style={{ flexShrink: 0, marginTop: '1px' }} />;
    case 'warnung': return <AlertCircle size={16} color={farbe} style={{ flexShrink: 0, marginTop: '1px' }} />;
    case 'ungeprueft': return <HelpCircle size={16} color={farbe} style={{ flexShrink: 0, marginTop: '1px' }} />;
    case 'hinweis': return <Info size={16} color={farbe} style={{ flexShrink: 0, marginTop: '1px' }} />;
    default: return <CheckCircle2 size={16} color={farbe} style={{ flexShrink: 0, marginTop: '1px' }} />;
  }
};

const REIHENFOLGE: Pruefstufe[] = ['sperre', 'ungeprueft', 'warnung', 'hinweis', 'ok'];

export const LohnlaufPruefungPanel: React.FC<LohnlaufPruefungPanelProps> = ({ ergebnis, laeuft, sperreAufgehoben = false }) => {
  if (laeuft) {
    return (
      <div style={{
        marginBottom: '16px',
        padding: '12px',
        backgroundColor: '#F9FAFB',
        borderRadius: '8px',
        border: '1px solid #E5E7EB',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <Loader2 size={16} color="#6B7280" style={{ flexShrink: 0 }} />
        <span style={{ fontSize: '13px', color: '#6B7280' }}>
          Lohnlaufunterlagen werden geprüft...
        </span>
      </div>
    );
  }

  if (!ergebnis || !ergebnis.aktiv) return null;

  const sortiert = [...ergebnis.befunde].sort(
    (links, rechts) => REIHENFOLGE.indexOf(links.stufe) - REIHENFOLGE.indexOf(rechts.stufe)
  );
  const anzahlSperren = ergebnis.befunde.filter(b => b.stufe === 'sperre').length;
  const kennzahlen = ergebnis.kennzahlen;

  const kopfStil = anzahlSperren > 0 ? STUFEN_STIL.sperre : STUFEN_STIL.ok;

  const kachel = (etikett: string, wert: string) => (
    <div>
      <p style={{
        fontSize: '11px',
        color: '#6B7280',
        margin: 0,
        textTransform: 'uppercase',
        letterSpacing: '0.03em'
      }}>
        {etikett}
      </p>
      <p style={{ fontSize: '13px', fontWeight: '600', color: '#111827', margin: '1px 0 0 0' }}>
        {wert}
      </p>
    </div>
  );

  return (
    <div style={{
      marginBottom: '16px',
      border: `1px solid ${kopfStil.rahmen}`,
      borderRadius: '8px',
      overflow: 'hidden'
    }}>
      {/* Kopfzeile mit Gesamturteil */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px',
        backgroundColor: kopfStil.flaeche
      }}>
        <ShieldCheck size={18} color={kopfStil.schrift} style={{ flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: '14px', fontWeight: '600', color: kopfStil.schrift, margin: 0 }}>
            {anzahlSperren > 0
              ? `Prüfung der Lohnlaufunterlagen: ${anzahlSperren} Beanstandung${anzahlSperren !== 1 ? 'en' : ''}`
              : 'Prüfung der Lohnlaufunterlagen bestanden'}
          </p>
          {anzahlSperren > 0 && (
            <p style={{ fontSize: '12px', color: kopfStil.schrift, margin: '2px 0 0 0', lineHeight: '1.4' }}>
              {sperreAufgehoben
                ? 'Die Sperre wurde für diesen Upload aufgehoben. Die Beanstandungen bleiben zur Kenntnis stehen.'
                : 'Der Upload ist gesperrt. Bitte die beanstandete Datei über das Papierkorbsymbol aus dem Paket entfernen oder die Unterlagen in DATEV korrigieren.'}
            </p>
          )}
        </div>
      </div>

      {/* Kennzahlen auf einen Blick */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: '10px 16px',
        padding: '12px',
        backgroundColor: '#F9FAFB',
        borderTop: `1px solid ${kopfStil.rahmen}`,
        borderBottom: '1px solid #E5E7EB'
      }}>
        {kachel('Mandant', kennzahlen.mandantennummer || '–')}
        {kachel('Kennziffer', kennzahlen.kennziffer || '–')}
        {kachel('Zeitraum', kennzahlen.zeitraum || '–')}
        {kachel('Lohnsumme', euro(kennzahlen.lohnsumme))}
        {kachel('Überweisungen', kennzahlen.anzahlUeberweisungen !== null ? String(kennzahlen.anzahlUeberweisungen) : '–')}
        {kachel('Lohnscheine', kennzahlen.anzahlLohnscheine !== null ? String(kennzahlen.anzahlLohnscheine) : '–')}
        {kachel('Ausführung', kennzahlen.ausfuehrungstermine.length > 0
          ? kennzahlen.ausfuehrungstermine.map(isoZuDeutsch).join(', ')
          : '–')}
        {kachel('Auftraggeberkonto', kennzahlen.auftraggeberIban ? ibanLesbar(kennzahlen.auftraggeberIban) : '–')}
      </div>

      {/* Einzelbefunde */}
      <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {sortiert.map((befund: Befund, index: number) => {
          const stil = STUFEN_STIL[befund.stufe];
          return (
            <div key={index} style={{
              display: 'flex',
              gap: '8px',
              padding: '8px 10px',
              backgroundColor: stil.flaeche,
              border: `1px solid ${stil.rahmen}`,
              borderRadius: '6px'
            }}>
              {stufenSymbol(befund.stufe, stil.schrift)}
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: '13px', fontWeight: '600', color: stil.schrift, margin: 0, lineHeight: '1.4' }}>
                  {befund.titel}
                </p>
                {befund.detail && (
                  <p style={{
                    fontSize: '12px',
                    color: '#4B5563',
                    margin: '2px 0 0 0',
                    lineHeight: '1.4',
                    wordBreak: 'break-word'
                  }}>
                    {befund.detail}
                  </p>
                )}
              </div>
            </div>
          );
        })}

        {ergebnis.nichtGeprueft.length > 0 && (
          <p style={{ fontSize: '12px', color: '#6B7280', margin: '2px 0 0 0', lineHeight: '1.4' }}>
            Nicht Teil der Prüfung, weil kein DATEV-Dateiname: {ergebnis.nichtGeprueft.join(', ')}
          </p>
        )}
      </div>
    </div>
  );
};

export default LohnlaufPruefungPanel;
